import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { Credentials } from "storefront/GrailedAPI/v1/Sessions/create";
import type { TwoFactorChallenge } from "storefront/GrailedAPI/NextAction";
import verifySignIn, {
  VerifySignInError,
} from "storefront/GrailedAPI/v1/Sessions/verify";
import {
  Form,
  FormControl,
  FormError,
  FormLabel,
  FormSubmit,
  Input,
} from "storefront/components/FormV2";
import Body from "storefront/components/Body";
import LinkButton from "storefront/components/LinkButton";
import SemiBoldBody from "storefront/components/SemiboldBody";
import { AuthenticationData } from "../Data";
import styles from "./TwoFactorChallengeForm.module.scss";

const CODE_LENGTH = 6;

type FormValues = {
  code: string;
};

type Props = {
  challenge: TwoFactorChallenge;
  credentials: Credentials;
  onSuccess: (data: AuthenticationData) => void;
  onSignInAgain: () => void;
};

const minutesUntil = (seconds: number): number =>
  Math.max(1, Math.ceil(seconds / 60));

const TwoFactorChallengeForm = ({
  challenge: initialChallenge,
  credentials,
  onSuccess,
  onSignInAgain,
}: Props) => {
  const formMethods = useForm<FormValues>({ defaultValues: { code: "" } });
  const { clearErrors, formState, setError, setFocus, setValue, watch } =
    formMethods;
  const [challenge, setChallenge] = useState(initialChallenge);
  const [mustSignInAgain, setMustSignInAgain] = useState(false);

  const code = watch("code");
  const errorMessage = formState.errors.code?.message;
  const isLocked = challenge.attemptsRemaining === 0;

  const isCodeEditable = !isLocked && !mustSignInAgain;

  // Focus would otherwise fall back to the modal itself, both on mount (the sign in form that had
  // focus is gone) and after each attempt (the input is disabled while submitting).
  useEffect(() => {
    if (!formState.isSubmitting && isCodeEditable) setFocus("code");
  }, [formState.isSubmitting, isCodeEditable, setFocus]);

  // Codes are only digits, so stripping everything else keeps a typo from using up an attempt
  // and lets pasted codes like "123 456" or "123-456" work.
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      "code",
      event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH),
    );
    clearErrors("code");
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const data = await verifySignIn({
        challengeToken: challenge.challengeToken,
        code: values.code,
        credentials,
      });
      onSuccess(data);
    } catch (error: unknown) {
      if (!(error instanceof VerifySignInError)) {
        setError("code", {
          message: new VerifySignInError({ type: "Unknown" }).message,
        });
        return;
      }

      switch (error.failure.type) {
        case "IncorrectCode":
        case "ChallengeLocked":
          setChallenge(error.failure.challenge);
          setValue("code", "");
          break;

        case "SignInAgain":
        case "Banned":
          setMustSignInAgain(true);
          break;

        default:
          break;
      }

      setError("code", { message: error.message });
    }
  };

  const renderStatus = () => {
    if (isLocked) {
      return (
        <Body className={styles.status}>
          You can sign in again in {minutesUntil(challenge.expiresIn)}{" "}
          {minutesUntil(challenge.expiresIn) === 1 ? "minute" : "minutes"}.
        </Body>
      );
    }

    if (errorMessage && !mustSignInAgain) {
      return (
        <Body className={styles.status}>
          {challenge.attemptsRemaining}{" "}
          {challenge.attemptsRemaining === 1 ? "attempt" : "attempts"} remaining
        </Body>
      );
    }

    return null;
  };

  return (
    <Form
      className={styles.twoFactorChallengeForm}
      formMethods={formMethods}
      onSubmit={handleSubmit}
    >
      <SemiBoldBody className={styles.title}>Check your email</SemiBoldBody>

      <Body className={styles.note}>
        For your security, enter the {CODE_LENGTH}-digit code we sent to{" "}
        {challenge.sentTo} to finish signing in.
      </Body>

      <FormControl className={styles.code} name="code">
        <FormLabel>Verification code</FormLabel>
        <Input
          autoComplete="one-time-code"
          inputMode="numeric"
          disabled={!isCodeEditable}
          rules={{ onChange: handleCodeChange }}
        />
        <div role="alert">
          <FormError />
          {renderStatus()}
        </div>
      </FormControl>

      <FormSubmit
        className={styles.submit}
        disabled={code.length !== CODE_LENGTH || !isCodeEditable}
      >
        Verify
      </FormSubmit>

      <LinkButton className={styles.signInAgain} onClick={onSignInAgain}>
        Back
      </LinkButton>
    </Form>
  );
};

export default TwoFactorChallengeForm;
